"use client";

import React, { ReactElement, useEffect, useState } from "react";
import { ChippyPromptImage } from "~app/components/ChippyPrompt/ChippyPromptWrapper";
import Layout from "~app/components/Layout/Layout";
import useUserDeviceName from "~app/hooks/useUserDeviceName";
import { UserStatus } from "~app/types";
import { Analytics } from "@vercel/analytics/react";
import ThemeProvider from "~app/providers/ThemeProvider";


const OnboardingPage = () => {
  const [isClient, setIsClient] = useState(false);
  const [userStatus, setUserStatus] = useState(UserStatus.onboarding);
  const deviceName = useUserDeviceName();

  useEffect(() => {
    setIsClient(true);
    if (isClient) {
      const handleExtensionMessage = (event: MessageEvent) => {
        // listen for message from Chippy
        if (event.data && event.data.from === "chippy") {
          // if any message from Chippy, set to complete
          setUserStatus(UserStatus.complete);
        }
      };

      window.addEventListener("message", handleExtensionMessage);

      return () => {
        window.removeEventListener("message", handleExtensionMessage);
      };
    }
  }, [isClient]);

  return (
    <>
      <Layout className="onboarding-container" hideNavbar>
        <div className="chippy-prompt">
          {userStatus === UserStatus.onboarding && (
            <>
              <div className="prompt-box prompt-box--onboarding">
                <p>
                  <b>You made it!</b>
                </p>
                <p>
                  Hit{" "}
                  <span className="command">
                    {deviceName === "windows" ? "Ctrl" : "⌘"}
                  </span>{" "}
                  +&nbsp;
                  <span className="command">J</span> to open or close me
                </p>
                {/* <!-- Message Arrow --> */}
                <svg
                  width="29px"
                  height="10px"
                  viewBox="0 0 29 10"
                  className="message-arrow"
                >
                  <title>Submit Message</title>
                  <g
                    stroke="none"
                    strokeWidth="1"
                    fill="none"
                    fillRule="evenodd"
                  >
                    <g
                      id="Rectangle-18"
                      transform="translate(0.468300, 0.000000)"
                      fill="currentColor"
                      fillRule="nonzero"
                    >
                      <path d="M27.8577,0 C23.2594,0 19.5317,3.728 19.5317,8.326 C19.5317,8.944 18.7847,9.254 18.3475,8.817 L16.2597,6.731 C11.946,2.421 6.0977,0 0,0 C6.47068611,0 11.3237007,0 14.5590438,0 C17.5143007,0 21.9471861,0 27.8577,0 Z" />
                    </g>
                  </g>
                </svg>
              </div>
              <div className="promptImage-wrapper">
                <ChippyPromptImage />
              </div>
            </>
          )}
        </div>
      </Layout>
      {/* I'm Here SVG */}
      <svg
        width="300"
        height="150"
        viewBox="0 0 139 95"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="onboarding-here"
      >
        <path
          d="M138.435 90.6029C138.571 90.3627 138.487 90.0575 138.247 89.9212L134.333 87.7005C134.093 87.5642 133.788 87.6484 133.651 87.8886C133.515 88.1288 133.599 88.434 133.839 88.5702L137.318 90.5442L135.344 94.0232C135.208 94.2634 135.292 94.5686 135.533 94.7048C135.773 94.8411 136.078 94.7569 136.214 94.5167L138.435 90.6029ZM22.5036 23.0595C24.7984 42.2026 38.1646 63.4321 58.6992 77.7288C79.2501 92.0368 107.015 99.4265 138.133 90.8381L137.867 89.8741C107.062 98.376 79.6016 91.063 59.2706 76.9081C38.9233 62.7418 25.7511 41.7481 23.4964 22.9405L22.5036 23.0595Z"
          fill="#ADADAD"
        />
        <path
          opacity="0.3"
          d="M2.51691 13.0239L1.70936 5.36639L2.78142 5.25334L3.58896 12.9109L2.51691 13.0239ZM4.84085 7.547L4.22825 7.6116L4.37113 5.08569L5.46506 4.97033L4.84085 7.547ZM8.0114 9.39166L8.3298 12.4109L7.25774 12.524L6.69015 7.14182L7.76221 7.02876L7.86488 8.00236C8.13765 7.23251 8.69146 6.82016 9.42439 6.74286C10.2448 6.65634 10.8287 6.94872 11.1139 7.65973C11.4085 6.88757 12.0061 6.4706 12.8046 6.38639C13.8876 6.27218 14.5781 6.8409 14.7131 8.12081L15.0903 11.698L14.0073 11.8122L13.6578 8.49756C13.5701 7.66617 13.2309 7.28162 12.5089 7.35776C11.8088 7.4316 11.2855 8.02877 11.3917 9.03519L11.7101 12.0544L10.638 12.1675L10.2884 8.85288C10.2008 8.02149 9.86157 7.63694 9.12864 7.71424C8.42852 7.78807 7.90526 8.38524 8.0114 9.39166ZM19.9554 8.13208L20.2738 11.1513L19.2017 11.2644L18.3596 3.27867L19.4316 3.16562L19.81 6.75372C20.0816 5.97293 20.6573 5.55827 21.4012 5.47982C22.5498 5.35869 23.2621 5.9251 23.3971 7.20501L23.7744 10.7822L22.6914 10.8964L22.3418 7.58176C22.2541 6.75037 21.893 6.36813 21.1054 6.45119C20.3834 6.52733 19.8492 7.12566 19.9554 8.13208ZM29.3939 8.55251C29.3682 9.67238 28.5917 10.3848 27.3337 10.5174C25.9225 10.6662 24.8952 9.94501 24.7279 8.3588L24.6437 7.56023C24.4776 5.98497 25.3209 5.06646 26.7539 4.91533C28.1323 4.76997 29.1072 5.51886 29.2756 7.116L29.3345 7.67391L25.812 8.04538C25.8402 8.20831 25.8673 8.36031 25.8933 8.50137C26.0408 9.27114 26.4952 9.69885 27.2937 9.61463C27.3594 9.60771 27.4129 9.591 27.4774 9.57314C28.0416 9.46939 28.3156 9.13079 28.3438 8.66326L29.3939 8.55251ZM25.7347 7.31244L25.8389 7.35676L28.2346 7.10411C28.0871 6.12418 27.6788 5.71374 26.8583 5.80026C26.016 5.88909 25.6787 6.46665 25.7347 7.31244ZM31.6022 7.0255L31.9079 9.92442L30.8359 10.0375L30.2683 4.65533L31.3403 4.54227L31.4384 5.47211C31.7146 4.73508 32.2368 4.33712 32.9369 4.26329L33.1994 4.2356L33.3102 5.28578L33.0039 5.31808C32.085 5.41499 31.4926 5.98626 31.6022 7.0255ZM38.7206 7.56895C38.6949 8.68882 37.9183 9.40119 36.6603 9.53386C35.2491 9.68268 34.2218 8.96144 34.0545 7.37524L33.9703 6.57667C33.8042 5.0014 34.6475 4.08289 36.0806 3.93176C37.4589 3.78641 38.4338 4.53529 38.6023 6.13243L38.6611 6.69034L35.1386 7.06181C35.1669 7.22475 35.194 7.37675 35.2199 7.5178C35.3675 8.28757 35.8218 8.71528 36.6204 8.63107C36.686 8.62414 36.7396 8.60744 36.8041 8.58958C37.3683 8.48583 37.6423 8.14723 37.6704 7.6797L38.7206 7.56895ZM35.0613 6.32888L35.1656 6.37319L37.5613 6.12054C37.4137 5.14062 37.0054 4.73017 36.185 4.8167C35.3426 4.90553 35.0053 5.48309 35.0613 6.32888ZM40.0654 6.87408L39.4097 1.39061L40.5692 1.26832L41.0827 6.76679L40.0654 6.87408ZM40.8085 9.0964C40.3819 9.14139 40.0663 8.87602 40.0225 8.46033C39.9786 8.04463 40.2342 7.74115 40.6609 7.69616C41.0984 7.65002 41.4117 7.89351 41.4555 8.3092C41.5005 8.73583 41.2461 9.05025 40.8085 9.0964Z"
          fill="#181818"
        />
      </svg>
    </>
  );
};

function OnboardingLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <html lang="en">
      <link rel="icon" type="image/x-icon" href="/window98.png" />
      <body>
        <ThemeProvider>{children}</ThemeProvider>
        <Analytics />
      </body>
    </html>
  );
}



OnboardingPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout>
      <OnboardingLayout>{page}</OnboardingLayout>
    </Layout>
  )
}


export default OnboardingPage;
